/* import __COLOCATED_TEMPLATE__ from './ai-prompt-data-typeahead.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { TypeaheadMatchResult } from '@intercom/composer-ember';
import type Composer from '@intercom/composer-ember/lib/composer';
import { NodeSelection } from 'prosemirror-state';
import { inject as service } from '@ember/service';
import type Operation from './editor';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  composer: Composer;
  matchResult: TypeaheadMatchResult | null;
  registerDelegate: (f: () => void) => void;
  deregisterDelegate: (f: () => void) => void;
  attrs: {
    allowedOperations?: Operation[];
  };
}

export default class AiPromptDataTypeahead extends Component<Args> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  get query(): string {
    return this.args.matchResult?.text || '';
  }

  get items(): { label: string; icon: string }[] {
    if (this.query.search('@') === -1 || this.query === '@ ') {
      return [];
    }

    let searchTerm = this.query.replace('@', '');

    let allowedOperations = this.args.attrs.allowedOperations;

    let items = [
      {
        id: 'use_action',
        // eslint-disable-next-line @intercom/intercom/no-bare-strings
        label: 'Connect with external system',
        icon: 'webhook',
      },
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      { id: 'read_attribute', label: 'Read an attribute', icon: 'attribute-titles' },
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      { id: 'update_attribute', label: 'Update an attribute', icon: 'path-up' },
      {
        id: 'add_tag_to_conversation',
        label: this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.inline-attribute.tag-conversation',
        ),
        icon: 'tag',
      },
      {
        id: 'escalate_conversation',
        label: this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.inline-attribute.escalate-conversation',
        ),
        icon: 'assignment',
      },
    ];

    items = allowedOperations?.length
      ? items.filter((item) => allowedOperations?.includes(item.id as unknown as Operation))
      : items;

    if (searchTerm) {
      return items.filter((item) => item.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    return items;
  }

  @action
  insertAiPromptData(data: { id: string }): void {
    let prosemirrorState = this.args.composer.state.prosemirrorState;

    if (prosemirrorState && this.args.matchResult) {
      let spec = prosemirrorState.schema.nodes.aiPromptData;
      let { tr } = prosemirrorState;

      // Delete the @ and any typed text
      tr.delete(this.args.matchResult.range.from, this.args.matchResult.range.to);

      // Create and insert the variable node
      let node = spec.createAndFill({
        operation: data.id,
      });
      if (node) {
        tr.insert(this.args.matchResult.range.from, node);
      }

      // Select the node we just inserted
      let nodePos = this.args.matchResult.range.from;
      let nodeSelection = NodeSelection.create(tr.doc, nodePos);
      tr.setSelection(nodeSelection);

      this.args.composer.commands.dispatch(tr);
      this.args.composer.commands.focus();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinCustom::AiPromptDataTypeahead': typeof AiPromptDataTypeahead;
    'workflows/graph-editor/node-items/steps/fin-custom/ai-prompt-data-typeahead': typeof AiPromptDataTypeahead;
  }
}
